
import React, { useState, useEffect } from "react";
import { DataTable } from "@/components/shared/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink, Copy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Factory {
  id: string;
  name: string;
  location?: string;
  contact_person?: string;
  active: boolean;
  capacity?: number;
  [key: string]: any;
}

interface FactoryListProps {
  factories: Factory[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (factory: Factory) => void;
  onDelete: (factory: Factory) => Promise<boolean>;
}

export const FactoryList = ({
  factories,
  isLoading,
  onAdd,
  onEdit,
  onDelete,
}: FactoryListProps) => {
  const [selectedFactory, setSelectedFactory] = useState<Factory | null>(null);
  const [showUrlDialog, setShowUrlDialog] = useState(false);
  const [baseUrl, setBaseUrl] = useState<string>("");
  const { toast } = useToast();

  useEffect(() => {
    fetchBaseUrl();
  }, []);

  const fetchBaseUrl = async () => {
    try {
      const { data, error } = await supabase
        .from("tenant_config")
        .select("domain")
        .maybeSingle();

      if (error) throw error;
      
      // Use domain from tenant_config if available, otherwise fallback to window.location.origin
      const domain = data?.domain || window.location.origin;
      setBaseUrl(domain);
    } catch (error) {
      console.error("Error fetching base URL:", error);
      // Fallback to current origin if there's an error
      setBaseUrl(window.location.origin);
    }
  };

  const generateFactoryUrl = (factory: Factory) => {
    const factoryName = factory.name.toLowerCase().replace(/\s+/g, '-');
    return `${baseUrl}/${factory.id}/${factoryName}`;
  };

  const handleFactoryUrlClick = (factory: Factory) => {
    setSelectedFactory(factory);
    setShowUrlDialog(true);
  };

  const handleCopyUrl = async () => {
    if (selectedFactory) {
      const url = generateFactoryUrl(selectedFactory);
      try {
        await navigator.clipboard.writeText(url);
        toast({
          title: "URL Copied",
          description: "Factory URL has been copied to clipboard.",
        });
      } catch (error) {
        toast({
          title: "Copy Failed",
          description: "Failed to copy URL to clipboard.",
          variant: "destructive",
        });
      }
    }
  };

  const factoryColumns = [
    {
      key: "name",
      label: "Factory Name",
    },
    {
      key: "location",
      label: "Location",
      render: (row: Factory) => row.location || "N/A"
    },
    {
      key: "contact_person",
      label: "Contact Person",
      render: (row: Factory) => row.contact_person || "N/A"
    },
    {
      key: "active",
      label: "Status",
      render: (row: Factory) => (
        <Badge className={row.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
          {row.active ? "Active" : "Inactive"}
        </Badge>
      )
    },
    {
      key: "factory_url",
      label: "Factory URL",
      render: (row: Factory) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleFactoryUrlClick(row)}
          className="flex items-center gap-2"
        >
          <ExternalLink className="h-4 w-4" />
          View URL
        </Button>
      )
    }
  ];

  return (
    <>
      <DataTable
        data={factories}
        columns={factoryColumns}
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={onDelete}
        title="Factories"
        itemName="Factory"
        isLoading={isLoading}
      />

      <Dialog open={showUrlDialog} onOpenChange={setShowUrlDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Factory URL - {selectedFactory?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="factory-url">Factory URL</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  id="factory-url"
                  value={selectedFactory ? generateFactoryUrl(selectedFactory) : ""}
                  readOnly
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyUrl}
                  className="flex items-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              This is the unique URL for this factory. You can copy and share this URL.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
